import { useEffect, useRef, useState } from 'react';

/**
 * Signing out a console nobody is at -- either door, Google or password --
 * after `limitMs` with no key, click, tap, wheel or mouse movement, and a
 * minute's warning first.
 *
 * KEPT HERE, NOT ON THE SERVER. An open screen polls every few seconds whether
 * anybody is looking at it, so to the server an abandoned tab and a busy one
 * look the same. Only the page can tell a person from a timer.
 *
 * ONE CLOCK FOR EVERY TAB. The last activity is kept in localStorage, so
 * working in one tab keeps the others signed in, and the warning in a tab you
 * are not looking at goes away when you move in the one you are. A private
 * window, where storage throws, keeps it per tab instead.
 *
 * A LATE CHECK STILL COUNTS. A hidden tab's timers are slowed to once a minute,
 * and a sleeping laptop's stop altogether, so the time is measured from the
 * stored moment rather than counted down. Coming back to a console idle past
 * the limit signs out before that first movement can count as activity -- it
 * would otherwise be what kept the session alive. So does opening the console
 * again after it was closed past the limit.
 */

const KEY = 'watch.lastActivity';
/** How long the warning is up before signing out. */
export const WARN_MS = 60_000;
/** Activity is written at most this often: mouse movement fires per pixel. */
const WRITE_EVERY_MS = 5_000;
// Never `scroll`: the page scrolls itself -- a support conversation keeps its
// newest message in view as the operator's arrive -- so it would let whoever
// is typing at the other end keep an empty desk signed in. A person scrolling
// sends a wheel, a touch, a key or a pointer on the scrollbar, all counted.
const EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

let memory = 0;
let written = 0;

/** The last activity in any tab, or null before there has been any. */
export function lastActivity(): number | null {
  let stored = 0;
  try {
    stored = Number(window.localStorage.getItem(KEY)) || 0;
  } catch {
    // Storage blocked: this tab's own memory is all there is.
  }
  const last = Math.max(stored, memory);
  return last > 0 ? last : null;
}

export function recordActivity(now = Date.now(), force = false) {
  memory = Math.max(memory, now);
  if (!force && now - written < WRITE_EVERY_MS) return;
  written = now;
  try {
    window.localStorage.setItem(KEY, String(now));
  } catch {
    // Kept in memory above; this tab still times out on its own.
  }
}

export type IdleState = { state: 'active' } | { state: 'warning'; leftMs: number } | { state: 'expired' };

/**
 * Where a signed-in console stands, `idleMs` after the last activity. Never
 * any activity counts as none yet -- a console signed in before this shipped
 * starts its clock now rather than signing out at once.
 */
export function idleState(idleMs: number, limitMs: number, warnMs = WARN_MS): IdleState {
  if (idleMs >= limitMs) return { state: 'expired' };
  const warnAt = Math.max(0, limitMs - warnMs);
  if (idleMs >= warnAt) return { state: 'warning', leftMs: limitMs - idleMs };
  return { state: 'active' };
}

/**
 * `signedIn`: null while it is not yet known (Firebase has not said), when
 * nothing is recorded -- a click on a loading page must not refresh the clock
 * of a session that is about to be found stale. False on the sign-in screen,
 * where every keystroke counts, so a fresh sign-in starts with a fresh clock.
 *
 * Answers how long is left while the warning is up, else null, and `stay` --
 * the warning's button, which is activity like any other.
 */
export function useIdleSignOut(signedIn: boolean | null, limitMs: number, signOut: () => void) {
  const [leftMs, setLeftMs] = useState<number | null>(null);
  const out = useRef(signOut);
  out.current = signOut;

  useEffect(() => {
    if (signedIn === null) return;
    let done = false;

    /** True when this check signed out. */
    const check = (now = Date.now()) => {
      if (!signedIn || done) return false;
      const last = lastActivity();
      if (last === null) {
        recordActivity(now, true);
        setLeftMs(null);
        return false;
      }
      const state = idleState(now - last, limitMs);
      if (state.state === 'expired') {
        done = true;
        setLeftMs(null);
        out.current();
        return true;
      }
      setLeftMs(state.state === 'warning' ? state.leftMs : null);
      return false;
    };

    // While the warning is up, activity is written straight through rather
    // than on the next write, so the other tabs' warnings close too.
    const warning = (now: number) => {
      const last = lastActivity();
      return last !== null && idleState(now - last, limitMs).state !== 'active';
    };
    const active = () => {
      const now = Date.now();
      if (check(now)) return;
      recordActivity(now, warning(now));
      if (signedIn) setLeftMs(null);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === KEY) check();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    check();
    // Once a second: the warning counts down by it. Cheap -- one storage read.
    const timer = window.setInterval(check, 1_000);
    for (const name of EVENTS) window.addEventListener(name, active, { capture: true, passive: true });
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(timer);
      for (const name of EVENTS) window.removeEventListener(name, active, { capture: true });
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [signedIn, limitMs]);

  useEffect(() => {
    if (!signedIn) setLeftMs(null);
  }, [signedIn]);

  return {
    leftMs,
    stay: () => {
      recordActivity(Date.now(), true);
      setLeftMs(null);
    },
  };
}
